import { useState, useEffect, useRef } from "react";
import {
  Container,
  TextInput,
  Group,
  Box,
  Stack,
  Text,
  Center,
  Button,
  Flex,
  Pagination,
  Badge,
} from "@mantine/core";
import { MultiStepLoader } from "../../components/shared/MultiStepLoader";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch, IconBookmark, IconSparkles } from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { successNotification } from "../../shared/utils/successNotification";
import CompanyCardHorizontal, { CompanyWithUI } from "../../components/shared/CompanyCardHorizontal";
import trpc from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util.ts";
import TitleComponent from "../../components/shared/TitleComponent";

import { useUserContext } from "../../context/UserContext.tsx";

const PAGE_SIZE = 10;

export default function PorfileDiscoverPage() {
  const navigate = useNavigate();
  const { shortlistedIds, addToShortlist, removeFromShortlist } = useUserContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchQuery, 700);
  const latestAiQueryRef = useRef<string>("");

  const [companies, setCompanies] = useState<CompanyWithUI[]>([]);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [isAiSearch, setIsAiSearch] = useState(false);

  // Initialize page from URL param or default to 1
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get("page");
    return pageParam ? parseInt(pageParam, 10) : 1;
  });

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const isFirstRun = useRef(true);

  // Update URL when page changes
  useEffect(() => {
    setSearchParams((prev) => {
      prev.set("page", page.toString());
      return prev;
    });
  }, [page, setSearchParams]);

  // Sync companies with shortlistedIds from context
  useEffect(() => {
    setCompanies((prev) =>
      prev.map((company) => ({
        ...company,
        isShortlisted: shortlistedIds.has(company.id),
      }))
    );
  }, [shortlistedIds]);

  const fetchCompanies = async (pageToFetch: number) => {
    setLoading(true);
    setLoadError(null);

    try {
      const response = await trpc.company.discover.query({
        page: pageToFetch,
        limit: PAGE_SIZE,
      });

      const mappedCompanies: CompanyWithUI[] = (response.companies || []).map((item: any) => ({
        ...item,
        isShortlisted: shortlistedIds.has(item.id),
      })) as CompanyWithUI[];

      setCompanies(mappedCompanies);
      setTotalCompanies(response.pagination?.total || 0);
    } catch (err) {
      handleTrpcError(err);
      setLoadError("Unable to load companies. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAiSearch = async (query: string) => {
    latestAiQueryRef.current = query;
    setLoading(true);
    setLoadError(null);
    setIsAiSearch(true);

    try {
      const response = await trpc.company.searchAI.query({ query });

      if (latestAiQueryRef.current !== query) return;

      // Extract results from nested structure
      const results = response?.result?.data?.json?.results || response.results || response.companies || [];

      // Helper to ensure array fields are always arrays
      const ensureArray = (value: any): any[] => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        return [value];
      };

      const mappedCompanies: CompanyWithUI[] = results.map((item: any) => {
        const companyId = item.company_id || item.id;

        return {
          id: companyId,
          gtid: item.gtid,
          legalName: item.legal_name || item.legalName,
          tradeName: item.trade_name || item.tradeName,
          registrationNumber: item.registration_number || item.registrationNumber,
          yearOfIncorporation: item.year_of_incorporation || item.yearOfIncorporation,
          website: item.company_website || item.website,
          email: item.director_email || item.email,
          phoneCountryCode: item.phone_country_code || item.phoneCountryCode,
          phoneNumber: item.phone_number || item.phoneNumber,
          description: item.description,
          industryCategory: ensureArray(item.industry_category || item.industryCategory),
          subIndustry: ensureArray(item.sub_industry || item.subIndustry),
          collaborationIntent: ensureArray(item.collaboration_intent || item.collaborationIntent),
          coreCapabilities: ensureArray(item.core_capabilities || item.coreCapabilities),
          secondaryCapabilities: ensureArray(item.secondary_capabilities || item.secondaryCapabilities),
          keyDifferentiators: item.key_differentiators || item.keyDifferentiators,
          technologies: ensureArray(item.technologies_infrastructure || item.technologies),
          headquartersCountry: item.country || item.headquartersCountry,
          headquartersCity: item.state || item.headquartersCity,
          currentAddress: item.headquarters_location || item.currentAddress,
          operatingCountries: ensureArray(item.operating_countries || item.operatingCountries),
          preferredCollaborationRegions: ensureArray(
            item.preferred_collaboration_regions || item.preferredCollaborationRegions
          ),
          status: item.status || "VERIFIED",
          // UI-specific fields
          isShortlisted: shortlistedIds.has(companyId),
        } as CompanyWithUI;
      });

      setCompanies(mappedCompanies);
      setTotalCompanies(mappedCompanies.length);
    } catch (err) {
      if (latestAiQueryRef.current !== query) return;
      handleTrpcError(err);
      setLoadError("AI search failed. Please try again.");
    } finally {
      if (latestAiQueryRef.current === query) {
        setLoading(false);
      }
    }
  };

  // Triggers on search query change. Skips first run to preserve URL page param.
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      fetchCompanies(page);
      return;
    }

    setPage(1);
    setCompanies([]);
    setLoadError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (debouncedSearch) {
      fetchAiSearch(debouncedSearch);
      return;
    }

    setIsAiSearch(false);
    fetchCompanies(1);
  }, [debouncedSearch]);

  // Handles user-initiated page changes only
  useEffect(() => {
    if (isFirstRun.current) return;
    if (page === 1) return;
    if (isAiSearch) return;

    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchCompanies(page);
  }, [page]);

  const handleShortlistToggle = async (companyId: string, isShortlisted: boolean) => {
    if (isShortlisted) {
      try {
        await addToShortlist(companyId);
        setCompanies((prev) => prev.map((c) => (c.id === companyId ? { ...c, isShortlisted: true } : c)));
        successNotification({ title: "Success", message: "Company added to shortlist" });
      } catch (err) {
        handleTrpcError(err);
      }
    } else {
      try {
        await removeFromShortlist(companyId);
        setCompanies((prev) => prev.map((c) => (c.id === companyId ? { ...c, isShortlisted: false } : c)));
        successNotification({ title: "Success", message: "Company removed from shortlist" });
      } catch (err) {
        handleTrpcError(err);
      }
    }
  };

  const totalPages = Math.ceil(totalCompanies / PAGE_SIZE);

  return (
    <Box mih="100vh" pb={20}>
      <TitleComponent
        title="Discover"
        description="Discover verified companies with advanced search criteria. Save favorites to your shortlist."
      />

      <Container fluid p="md">
        {/* Desktop: Search + Shortlist */}
        <Group gap="md" align="flex-end" visibleFrom="sm">
          <TextInput
            placeholder="Search by capability, industry, or keyword..."
            leftSection={<IconSearch size={18} />}
            size="md"
            radius="lg"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
        </Group>

        {/* Mobile: Search + Shortlist */}
        <Box hiddenFrom="sm">
          <TextInput
            placeholder="Search by capability, industry, or keyword..."
            leftSection={<IconSearch size={18} />}
            size="md"
            radius="lg"
            mb="sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
          />

          <Button
            component="a"
            href="/shortlist"
            onClick={(e) => {
              e.preventDefault();
              navigate("/shortlist");
            }}
            leftSection={<IconBookmark size={18} />}
            variant="filled"
            fullWidth
            mb="md"
            radius="lg"
          >
            Shortlist
          </Button>
        </Box>
      </Container>

      <Container fluid px="md">
        {isAiSearch && (
          <Flex align="center" gap="sm" mt="lg" mb="lg">
            <Badge leftSection={<IconSparkles size={12} />} color="violet" variant="light" size="sm">
              AI Search
            </Badge>
          </Flex>
        )}

        <Box style={{ position: "relative", minHeight: 200 }} mt={isAiSearch ? 0 : "lg"}>
          <MultiStepLoader
            loading={loading}
            inline
            minimal
            loadingStates={[
              { text: "Searching companies..." },
              { text: "Analyzing your query..." },
              { text: "Finding best matches..." },
              { text: "Ranking results..." },
            ]}
            title=""
            subtitle=""
            textSize="lg"
            duration={1500}
          />

          {companies.length === 0 && !loading ? (
            <Center py="xl">
              <Text c="slate.6">No companies found</Text>
            </Center>
          ) : (
            <Stack gap="md">
              {companies.map((company) => (
                <CompanyCardHorizontal
                  key={company.id}
                  company={company}
                  isDiscoverStyle
                  onShortlistClick={handleShortlistToggle}
                  onViewDetails={(id) => navigate(`/my-discovery/${id}`)}
                />
              ))}
            </Stack>
          )}
        </Box>

        {loadError && (
          <Center py="md">
            <Text c="red" size="sm">
              {loadError}
            </Text>
          </Center>
        )}

        {totalPages > 1 && !isAiSearch && (
          <Center mt="xl">
            <Pagination total={totalPages} value={page} onChange={setPage} size="md" radius="md" />
          </Center>
        )}
      </Container>
    </Box>
  );
}
